///
/// EarthBrowser
///
/// Created by Matt Giger on 1/3/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import type { BuoySteepness, DartMeasurementType } from "@shared-types/buoy"

export interface BuoyObservationProperties {
	station_id: string
	time: string
	wind_direction: number | null
	wind_speed: number | null
	wind_gust: number | null
	air_temp: number | null
	pressure: number | null
	pressure_tendency: number | null
	visibility: number | null
	dewpoint: number | null
	wave_height: number | null
	wave_period: number | null
	avg_wave_period: number | null
	wave_direction: number | null
	water_temp: number | null
	lat: number | null
	lon: number | null
	swell_height: number | null
	swell_period: number | null
	swell_direction: number | null
	wind_wave_height: number | null
	wind_wave_period: number | null
	wind_wave_direction: number | null
	steepness: BuoySteepness | null
	water_column_height: number | null
	dart_measurement_type: DartMeasurementType | null
	water_level: number | null
}

const cBatchSize = 100

const cColumns = `station_id, time, wind_direction, wind_speed, wind_gust,
	air_temp, pressure, pressure_tendency, visibility, dewpoint,
	wave_height, wave_period, avg_wave_period, wave_direction, water_temp,
	lat, lon,
	swell_height, swell_period, swell_direction,
	wind_wave_height, wind_wave_period, wind_wave_direction, steepness,
	water_column_height, dart_measurement_type, water_level`

const cColumnCount = 27

export class BuoyObservationsTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists buoy_observations (
					station_id text not null,
					time text not null,
					wind_direction real,
					wind_speed real,
					wind_gust real,
					air_temp real,
					pressure real,
					pressure_tendency real,
					visibility real,
					dewpoint real,
					wave_height real,
					wave_period real,
					avg_wave_period real,
					wave_direction real,
					water_temp real,
					lat real,
					lon real,
					swell_height real,
					swell_period real,
					swell_direction real,
					wind_wave_height real,
					wind_wave_period real,
					wind_wave_direction real,
					steepness text,
					water_column_height real,
					dart_measurement_type integer,
					water_level real,
					primary key (station_id, time)
				)`
			},
			{ sql: "create index if not exists idx_buoy_observations_station on buoy_observations(station_id)" },
			{ sql: "create index if not exists idx_buoy_observations_time on buoy_observations(time desc)" },
		])
	}

	public static async replaceAll(rows: BuoyObservationProperties[]): Promise<void> {
		await Database.db.exec("delete from buoy_observations", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: BuoyObservationProperties[]): Promise<void> {
		const statement = `insert into buoy_observations (${cColumns})
			values (${Array(cColumnCount).fill("?").join(", ")})
			on conflict(station_id, time) do update set
				wind_direction = coalesce(excluded.wind_direction, buoy_observations.wind_direction),
				wind_speed = coalesce(excluded.wind_speed, buoy_observations.wind_speed),
				wind_gust = coalesce(excluded.wind_gust, buoy_observations.wind_gust),
				air_temp = coalesce(excluded.air_temp, buoy_observations.air_temp),
				pressure = coalesce(excluded.pressure, buoy_observations.pressure),
				pressure_tendency = coalesce(excluded.pressure_tendency, buoy_observations.pressure_tendency),
				visibility = coalesce(excluded.visibility, buoy_observations.visibility),
				dewpoint = coalesce(excluded.dewpoint, buoy_observations.dewpoint),
				wave_height = coalesce(excluded.wave_height, buoy_observations.wave_height),
				wave_period = coalesce(excluded.wave_period, buoy_observations.wave_period),
				avg_wave_period = coalesce(excluded.avg_wave_period, buoy_observations.avg_wave_period),
				wave_direction = coalesce(excluded.wave_direction, buoy_observations.wave_direction),
				water_temp = coalesce(excluded.water_temp, buoy_observations.water_temp),
				lat = coalesce(excluded.lat, buoy_observations.lat),
				lon = coalesce(excluded.lon, buoy_observations.lon),
				swell_height = coalesce(excluded.swell_height, buoy_observations.swell_height),
				swell_period = coalesce(excluded.swell_period, buoy_observations.swell_period),
				swell_direction = coalesce(excluded.swell_direction, buoy_observations.swell_direction),
				wind_wave_height = coalesce(excluded.wind_wave_height, buoy_observations.wind_wave_height),
				wind_wave_period = coalesce(excluded.wind_wave_period, buoy_observations.wind_wave_period),
				wind_wave_direction = coalesce(excluded.wind_wave_direction, buoy_observations.wind_wave_direction),
				steepness = coalesce(excluded.steepness, buoy_observations.steepness),
				water_column_height = coalesce(excluded.water_column_height, buoy_observations.water_column_height),
				dart_measurement_type = coalesce(excluded.dart_measurement_type, buoy_observations.dart_measurement_type),
				water_level = coalesce(excluded.water_level, buoy_observations.water_level)`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(o => [
				o.station_id,
				o.time,
				o.wind_direction,
				o.wind_speed,
				o.wind_gust,
				o.air_temp,
				o.pressure,
				o.pressure_tendency,
				o.visibility,
				o.dewpoint,
				o.wave_height,
				o.wave_period,
				o.avg_wave_period,
				o.wave_direction,
				o.water_temp,
				o.lat,
				o.lon,
				o.swell_height,
				o.swell_period,
				o.swell_direction,
				o.wind_wave_height,
				o.wind_wave_period,
				o.wind_wave_direction,
				o.steepness,
				o.water_column_height,
				o.dart_measurement_type,
				o.water_level,
			]))
		}
	}

	/**
	 * Get latest observation for each station
	 */
	public static async getLatestByStation(): Promise<Map<string, BuoyObservationProperties>> {
		const rows = await Database.db.exec(
			`select ${cColumns}
			from buoy_observations
			where (station_id, time) in (
				select station_id, max(time) from buoy_observations group by station_id
			)`,
			[],
			"low"
		) as BuoyObservationProperties[]

		const map = new Map<string, BuoyObservationProperties>()
		for (const row of rows) {
			map.set(row.station_id, row)
		}
		return map
	}

	/**
	 * Get all observations for a specific station
	 */
	public static async getByStationId(stationId: string, limit: number = 100): Promise<BuoyObservationProperties[]> {
		const rows = await Database.db.exec(
			`select ${cColumns}
			from buoy_observations
			where station_id = ?
			order by time desc
			limit ?`,
			[stationId, limit],
			"low"
		)
		return rows as BuoyObservationProperties[]
	}

	/**
	 * Get latest observation for a specific station
	 */
	public static async getLatestForStation(stationId: string): Promise<BuoyObservationProperties | null> {
		const rows = await Database.db.exec(
			`select ${cColumns}
			from buoy_observations
			where station_id = ?
			order by time desc
			limit 1`,
			[stationId],
			"low"
		) as BuoyObservationProperties[]

		return rows[0] ?? null
	}

	/**
	 * Latest observation per station for a set of stations in one query
	 * (the storm column pins a dozen at once; one round trip through the
	 * worker queue instead of one per station)
	 */
	public static async getLatestForStations(stationIds: string[]): Promise<Map<string, BuoyObservationProperties>> {
		const result = new Map<string, BuoyObservationProperties>()
		if (stationIds.length === 0) return result
		const placeholders = stationIds.map(() => "?").join(", ")
		const rows = await Database.db.exec(
			`select ${cColumns}
			from buoy_observations o
			where station_id in (${placeholders})
				and time = (select max(time) from buoy_observations where station_id = o.station_id)`,
			stationIds,
			"high"
		) as BuoyObservationProperties[]
		for (const row of rows) {
			if (!result.has(row.station_id)) result.set(row.station_id, row)
		}
		return result
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from buoy_observations`,
			[],
			"low"
		) as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}

	/**
	 * Delete observations older than a certain time
	 */
	public static async deleteOlderThan(isoTime: string): Promise<number> {
		const result = await Database.db.exec(
			`delete from buoy_observations where time < ? returning station_id`,
			[isoTime],
			"critical"
		) as Array<{ station_id: string }>
		return result.length
	}
}
